/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { ofType, StateObservable } from 'redux-observable';
import { Action, ActionCreators, ChecksumPayload, ContentPayload } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import { hashEmailSha256 } from '../../utils/HashUtils';
import { SEC_TO_MS_CONVERSION } from '../../utils/DateTimeUtils';

const getWhatsNewPollingIntervalSeconds = (state: RootState): number =>
  state.config.appConfig.whatsNewPanelPollingIntervalSeconds ?? 300;

const receiveLatestChecksumSuccess = (response: AjaxResponse<ChecksumPayload>): any =>
  ActionCreators.ReceivedLatestChecksumSuccess.create({ checksum: response.response.checksum });

const receiveLatestChecksumError = (error: AjaxError): any => ActionCreators.ReceivedLatestChecksumError.create(error);

const receiveUserChecksumSuccess = (response: AjaxResponse<Partial<ChecksumPayload>>): any =>
  ActionCreators.ReceivedUserChecksumSuccess.create({ checksum: response.response?.checksum });

const receiveUserChecksumError = (error: any): any => ActionCreators.ReceivedUserChecksumError.create(error);

const receiveContentSuccess = (response: AjaxResponse<ContentPayload>): any =>
  ActionCreators.ReceivedWhatsNewContentSuccess.create({
    checksum: response.response.checksum,
    markdownContent: response.response.markdownContent,
  });

const receiveContentError = (error: AjaxError): any => ActionCreators.ReceivedWhatsNewContentError.create(error);

const receiveMarkViewedSuccess = (checksum: string): any =>
  ActionCreators.UpdateUserLastViewedChecksumSuccess.create({ checksum });

const receiveMarkViewedError = (error: any): any => ActionCreators.UpdateUserLastViewedChecksumError.create(error);

const shouldRequestWhatsNewContent = (state: RootState): boolean => {
  const latestChecksum = state.whatsNew.latestChecksum;
  const userLastViewedChecksum = state.whatsNew.userLastViewedChecksum;

  if (!latestChecksum || latestChecksum === userLastViewedChecksum) {
    return false;
  }

  return !state.whatsNew.isRequestingContent && state.whatsNew.contentChecksum !== latestChecksum;
};

export const requestLatestChecksumPollEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestLatestChecksumPoll.type),
    switchMap($action =>
      timer(0, getWhatsNewPollingIntervalSeconds(state$.value) * SEC_TO_MS_CONVERSION).pipe(
        map(() => $action),
        takeUntil(action$.pipe(ofType(ActionCreators.CancelLatestChecksumPoll.type))),
      ),
    ),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.getFromBasePath('/whats-new-panel/checksum').pipe(
          map((response: AjaxResponse<ChecksumPayload>): Action => receiveLatestChecksumSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(receiveLatestChecksumError(error))),
          ),
        ),
    ),
  );

export const requestUserChecksumEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestUserChecksum.type),
    switchMap(
      ($action: any): Observable<Action> =>
        from(hashEmailSha256($action.payload.userEmail)).pipe(
          switchMap(
            (hashedEmail: string): Observable<Action> =>
              ajaxRaw.getFromBasePath(`/whats-new-panel/checksum/${hashedEmail}`).pipe(
                map((response: AjaxResponse<Partial<ChecksumPayload>>): Action => receiveUserChecksumSuccess(response)),
                catchError(
                  (error: AjaxError): Observable<Action> =>
                    handleErrorInterceptor(error, of(receiveUserChecksumError(error))),
                ),
              ),
          ),
          catchError((error: any): Observable<Action> => of(receiveUserChecksumError(error))),
        ),
    ),
  );

export const requestWhatsNewContentEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestWhatsNewContent.type),
    switchMap(
      (): Observable<Action> =>
        ajaxRaw.getFromBasePath('/whats-new-panel/content').pipe(
          map((response: AjaxResponse<ContentPayload>): Action => receiveContentSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(receiveContentError(error))),
          ),
        ),
    ),
  );

export const requestContentOnChecksumMismatchEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(
      ActionCreators.ReceivedLatestChecksumSuccess.type,
      ActionCreators.ReceivedUserChecksumSuccess.type,
      ActionCreators.UpdateUserLastViewedChecksumSuccess.type,
    ),
    switchMap((): Observable<Action> => {
      if (!shouldRequestWhatsNewContent(state$.value)) {
        return EMPTY;
      }

      return of<any>(ActionCreators.RequestWhatsNewContent.create());
    }),
  );

export const requestUpdateUserLastViewedChecksumEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestUpdateUserLastViewedChecksum.type),
    switchMap(
      ($action: any): Observable<Action> =>
        from(hashEmailSha256($action.payload.userEmail)).pipe(
          switchMap(
            (hashedEmail: string): Observable<Action> =>
              ajaxRaw
                .postToBasePath(`/whats-new-panel/checksum/${hashedEmail}`, {
                  checksum: $action.payload.checksum,
                })
                .pipe(
                  map((): Action => receiveMarkViewedSuccess($action.payload.checksum)),
                  catchError(
                    (error: AjaxError): Observable<Action> =>
                      handleErrorInterceptor(error, of(receiveMarkViewedError(error))),
                  ),
                ),
          ),
          catchError((error: any): Observable<Action> => of(receiveMarkViewedError(error))),
        ),
    ),
  );
